/**
 * Portal branding — fetches and caches the tenant's visual identity settings.
 * Called once on app startup (before login) via GET /customer/portal/settings.
 *
 * Whitelabel fields:
 *   name          — portal display name (page title, login card header)
 *   logoUrl       — absolute URL to tenant logo
 *   primaryColor  — hex colour for buttons, focus rings, primary accents
 *   secondaryColor— hex colour for outline buttons, links, badges
 *   fontFamily    — Google Fonts family name (e.g. "Poppins")
 *   faviconUrl    — absolute URL to favicon
 */

export interface PortalBranding {
  name: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  faviconUrl: string | null;
  /** ID of the default CMS space for this tenant's portal */
  defaultSpaceId: string | null;
}

export const BRANDING_DEFAULTS: PortalBranding = {
  name: 'Customer Portal',
  logoUrl: null,
  primaryColor: '#4F46E5',
  secondaryColor: '#0F172A',
  fontFamily: 'Inter',
  faviconUrl: null,
  defaultSpaceId: null,
};

/**
 * List of supported Google Fonts families.
 * "Inter" is loaded via CDN as a fallback — all others are loaded dynamically.
 */
export const SUPPORTED_FONTS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Poppins',
  'Montserrat',
  'Nunito',
  'Raleway',
  'Source Sans 3',
  'Work Sans',
  'DM Sans',
  'Plus Jakarta Sans',
] as const;

export type SupportedFont = (typeof SUPPORTED_FONTS)[number];

function getTenantSlug(): string {
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug) return envSlug;
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 2 && parts[0] !== 'localhost') return parts[0];
  return 'tenant1';
}

export async function fetchBranding(): Promise<PortalBranding> {
  try {
    const res = await fetch('/customer/portal/settings', {
      headers: { 'x-tenant-slug': getTenantSlug() },
    });
    if (!res.ok) return BRANDING_DEFAULTS;
    const data = (await res.json()) as Partial<PortalBranding>;
    return {
      name: data.name || BRANDING_DEFAULTS.name,
      logoUrl: data.logoUrl || null,
      primaryColor: data.primaryColor || BRANDING_DEFAULTS.primaryColor,
      secondaryColor: data.secondaryColor || BRANDING_DEFAULTS.secondaryColor,
      fontFamily: data.fontFamily || BRANDING_DEFAULTS.fontFamily,
      faviconUrl: data.faviconUrl || null,
      defaultSpaceId: data.defaultSpaceId || null,
    };
  } catch {
    return BRANDING_DEFAULTS;
  }
}

/**
 * Inject a Google Fonts <link> into <head> for the given family.
 * No-ops if the font is "Inter" (already covered by the CDN link in index.html)
 * or if a link for this family already exists.
 */
function loadGoogleFont(family: string): void {
  if (family === 'Inter') return;
  const id = `gf-${family.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  const encoded = encodeURIComponent(family);
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

const RADIUS_MAP: Record<string, string> = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '16px',
  full: '9999px',
};

/**
 * Apply per-space theme overrides on top of portal branding.
 * Called when a CMS page loads; pass portalBranding to restore defaults on unmount.
 */
export function applySpaceTheme(
  theme: Partial<Record<string, string>>,
  portalBranding: PortalBranding,
): void {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', theme.primaryColor ?? portalBranding.primaryColor);
  root.style.setProperty('--color-secondary', theme.secondaryColor ?? portalBranding.secondaryColor);
  if (theme.accentColor) {
    root.style.setProperty('--color-accent', theme.accentColor);
  } else {
    root.style.removeProperty('--color-accent');
  }
  if (theme.backgroundColor) {
    root.style.setProperty('--color-bg', theme.backgroundColor);
  } else {
    root.style.removeProperty('--color-bg');
  }
  const fontFamily = theme.fontFamily ?? portalBranding.fontFamily;
  if (fontFamily !== portalBranding.fontFamily) loadGoogleFont(fontFamily);
  root.style.setProperty('--font-family', `"${fontFamily}", Inter, system-ui, sans-serif`);
  if (theme.borderRadius) {
    root.style.setProperty('--radius', RADIUS_MAP[theme.borderRadius] ?? theme.borderRadius);
  } else {
    root.style.removeProperty('--radius');
  }
}

/**
 * Fetch the active theme for the space identified by `spaceId`.
 * Returns null if no space ID is available or the request fails.
 */
export async function fetchSpaceTheme(
  spaceId: string,
): Promise<Partial<Record<string, string>> | null> {
  try {
    const res = await fetch('/customer/portal/theme', {
      headers: {
        'x-tenant-slug': getTenantSlug(),
        'x-space-id': spaceId,
      },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { theme?: Partial<Record<string, string>> };
    return data.theme ?? null;
  } catch {
    return null;
  }
}


/** Apply branding to the document (title, favicon, CSS custom properties, font). */
export function applyBranding(branding: PortalBranding): void {
  const root = document.documentElement;

  // Colours as CSS custom properties — consumed by .btn-primary, .btn-outline, etc.
  root.style.setProperty('--color-primary', branding.primaryColor);
  root.style.setProperty('--color-secondary', branding.secondaryColor);

  // Font family
  loadGoogleFont(branding.fontFamily);
  root.style.setProperty('--font-family', `"${branding.fontFamily}", Inter, system-ui, sans-serif`);

  // Page title
  document.title = branding.name;

  // Favicon
  if (branding.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }
}
